import type { MoneyV2 } from "types/storefront.types";
import type { ProductCommonFieldsFragment } from "types/storefront.generated";

import type { GoogleAnalyticsItem } from "types/events";

import type { ElasticProduct } from "@usereactify/search";

import type { Site } from "./sites";

/**
 * Generate an "item" used in Google Analytics events.
 * @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce#implementation
 */
export class GoogleAnalyticsItemResolver {
  readonly site: Site;

  constructor({ site }: { site: Site }) {
    this.site = site;
  }

  /**
   * Resolve item from the given Shopify product fragment.
   * Prices are not pulled directly from the fragment as their calculations can be more complex.
   */
  resolveFromShopifyProduct({
    price,
    shopifyProduct,
  }: {
    price: MoneyV2;
    compareAtPrice?: MoneyV2 | null;
    shopifyProduct: ProductCommonFieldsFragment;
  }): GoogleAnalyticsItem {
    return {
      item_id: shopifyProduct.id.replace("gid://shopify/Product/", ""),
      item_name: shopifyProduct.title,
      item_style: shopifyProduct.styleNameMetafield?.value,
      item_category: shopifyProduct.classMetafield?.value,
      item_category2: shopifyProduct.categoryMetafield?.value,
      item_category3: shopifyProduct.subcategoryMetafield?.value,
      item_category4: shopifyProduct.statusMetafield?.value,
      price: price.amount.toString(),
      /** @todo Calculate discount attribute? */
    };
  }

  /**
   * Resolve item from the given Reactify product.
   */
  resolveFromReactifyProduct({
    reactifyProduct,
  }: {
    reactifyProduct: ElasticProduct;
  }): GoogleAnalyticsItem {
    return {
      item_id: `${reactifyProduct.id}`,
      item_name: reactifyProduct.title,
      item_style: reactifyProduct.metafields?.find(
        (metafield) => metafield.key === "netsuite_style_name",
      )?.value,
      item_category: reactifyProduct.metafields?.find(
        (metafield) => metafield.key === "netsuite_class",
      )?.value,
      item_category2: reactifyProduct.metafields?.find(
        (metafield) => metafield.key === "netsuite_category",
      )?.value,
      item_category3: reactifyProduct.metafields?.find(
        (metafield) => metafield.key === "netsuite_sub_category",
      )?.value,
      item_category4: reactifyProduct.metafields?.find(
        (metafield) => metafield.key === "netsuite_status",
      )?.value,
      price:
        reactifyProduct.presentment_price_ranges?.max_variant_price
          ?.find((price) => price.currency_code === this.site.currencyCode)
          ?.amount?.toString() ??
        reactifyProduct.variants?.[0].price.toString() ??
        "0",
    };
  }
}
